// @ts-strict-ignore
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { Line } from '@3d-scene/model/line';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { PasteArticlePresenter } from '../presenter/paste-article.presenter';
import { OverlapInfo } from '@3d-scene/model/overlap-info';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';

export class RangeOnLineArticleData {
  public baseUsageId: string;
  public baseArticleSelected: boolean = false;
  public insertHelper: InsertArticleHelper;
  private _articlesOnRail: ArticleContainer[] = null;
  private _selectedArticlesOnRail: ArticleContainer[] = [];
  private _selectedArticlesOnRailSorted: boolean = false;
  private _lastArticleBeforeSelection: ArticleContainer = null;
  private _firstArticleAfterSelection: ArticleContainer = null;

  constructor(
    private articleContainerUtils: ArticleContainerUtils,
    insertArticleHelperFactory: InsertArticleHelperFactory,
    baseContainer: ArticleContainer = null
  ) {
    if (baseContainer) {
      this.baseUsageId = baseContainer.articleGUID;
      this.insertHelper = insertArticleHelperFactory.createInstanceByRangeOnLineArticleContainer(baseContainer);
    }
  }

  public get articlesOnRail(): ArticleContainer[] {
    if (!this._articlesOnRail) {
      this._articlesOnRail = this.insertHelper === null ? [] : this.insertHelper.getAllConnectedArticles(true);
    }
    return this._articlesOnRail;
  }

  public get selectedArticlesOnRail(): ArticleContainer[] {
    if (!this._selectedArticlesOnRailSorted) {
      if (this._selectedArticlesOnRail.length > 0) {
        this._selectedArticlesOnRail = this.insertHelper.sortArticles(this._selectedArticlesOnRail);
      }
      this._selectedArticlesOnRailSorted = true;
    }
    return this._selectedArticlesOnRail;
  }

  public get lastArticleBeforeSelection(): ArticleContainer {
    if (!this._lastArticleBeforeSelection) {
      this._lastArticleBeforeSelection = this.findLastArticleBeforeSelection();
    }
    return this._lastArticleBeforeSelection;
  }

  public get firstArticleAfterSelection(): ArticleContainer {
    if (!this._firstArticleAfterSelection) {
      this._firstArticleAfterSelection = this.findFirstArticleAfterSelection();
    }
    return this._firstArticleAfterSelection;
  }

  public get lastSelectedArticle(): ArticleContainer {
    return this.selectedArticlesOnRail.length > 0 ? this.selectedArticlesOnRail[this.selectedArticlesOnRail.length - 1] : null;
  }

  public get firstSelectedArticle(): ArticleContainer {
    return this.selectedArticlesOnRail.length > 0 ? this.selectedArticlesOnRail[0] : null;
  }

  public storeSelectedArticleOnRail(articleContainer: ArticleContainer): void {
    this._selectedArticlesOnRail.push(articleContainer);
    this._selectedArticlesOnRailSorted = false;
  }

  public isOperationSupported(pasteOperation: number): boolean {
    let isSupported: boolean = false;
    switch (pasteOperation) {
      case PasteArticlePresenter.PASTE_AT_START:
        isSupported = true;
        break;
      case PasteArticlePresenter.PASTE_AFTER_LAST_ARTICLE_ON_RAIL:
        isSupported = this.articlesOnRail.length > 0;
        break;
      case PasteArticlePresenter.PASTE_AFTER_EACH_SELECTED_ARTICLE:
        isSupported = this.selectedArticlesOnRail.length > 0;
        break;
      case PasteArticlePresenter.PASTE_AFTER_LAST_ARTICLE_BEFORE_SELECTION:
        isSupported = this.lastArticleBeforeSelection != null;
        break;
    }
    return isSupported;
  }

  private findNeighborArticleByPasteOperation(pasteOperation: number, isBuildingBlock: boolean = false): ArticleContainer {
    let neighborArticle: ArticleContainer = null;
    if (isBuildingBlock && this.selectedArticlesOnRail.length === 0) {
      pasteOperation = PasteArticlePresenter.PASTE_AFTER_LAST_ARTICLE_ON_RAIL;
    }
    switch (pasteOperation) {
      case PasteArticlePresenter.PASTE_AFTER_LAST_ARTICLE_ON_RAIL:
        if (this.articlesOnRail.length > 0) {
          neighborArticle = this.articlesOnRail[this.articlesOnRail.length - 1] as ArticleContainer;
        }
        break;
      case PasteArticlePresenter.PASTE_AFTER_EACH_SELECTED_ARTICLE:
        if (this.selectedArticlesOnRail.length > 0) {
          neighborArticle = this.selectedArticlesOnRail[this.selectedArticlesOnRail.length - 1] as ArticleContainer;
        }
        break;
      case PasteArticlePresenter.PASTE_AFTER_LAST_ARTICLE_BEFORE_SELECTION:
        neighborArticle = this.lastArticleBeforeSelection;
        break;
    }
    return neighborArticle;
  }

  public calculateStartOffset(
    pasteOperation: number,
    offset: number,
    copiedOverlapInfo: OverlapInfo = null,
    isBuildingBlock: boolean = false
  ): number {
    let startOffset: number = this.insertHelper.getConfiguredStartOffset();
    const neighborArticle: ArticleContainer = this.findNeighborArticleByPasteOperation(pasteOperation, isBuildingBlock);
    if (neighborArticle != null) {
      const articleLine: AdvancedLine = this.insertHelper.getArticleLine(neighborArticle);
      const dummyLine: AdvancedLine = new AdvancedLine(null, null, copiedOverlapInfo);
      const overlapLength: number = AdvancedLine.calculateMaxOverlapLength(articleLine, dummyLine);
      startOffset += this.insertHelper.getDistanceFromConfiguredStart(articleLine.end) - overlapLength;
    }
    startOffset += offset;
    return startOffset;
  }

  private findLastArticleBeforeSelection(): ArticleContainer {
    if (this.selectedArticlesOnRail == null || this.selectedArticlesOnRail.length === 0) {
      return null;
    }
    const commonLine: Line = this.insertHelper.calculateCommonArticlesLine(this.selectedArticlesOnRail);
    let nearestArticle: ArticleContainer = null;
    let nearestLine: Line = null;
    for (const articleContainer of this.articlesOnRail) {
      if (this.articleContainerUtils.findArticleContainerByGUID(articleContainer.articleGUID, this.selectedArticlesOnRail) != null) {
        continue;
      }
      const articleLine: Line = this.insertHelper.getArticleLine(articleContainer);
      if (!articleLine || this.insertHelper.getDistanceFromStart(commonLine.start) < this.insertHelper.getDistanceFromStart(articleLine.start)) {
        continue;
      }
      if (!nearestLine || articleLine.end.distanceTo(commonLine.start) < nearestLine.end.distanceTo(commonLine.start)) {
        nearestLine = articleLine;
        nearestArticle = articleContainer;
      }
    }
    return nearestArticle;
  }

  private findFirstArticleAfterSelection(): ArticleContainer {
    if (this.selectedArticlesOnRail == null || this.selectedArticlesOnRail.length === 0) {
      return null;
    }
    const commonLine: Line = this.insertHelper.calculateCommonArticlesLine(this.selectedArticlesOnRail);
    let nearestArticle: ArticleContainer = null;
    let nearestLine: Line = null;
    for (const articleContainer of this.articlesOnRail) {
      if (this.articleContainerUtils.findArticleContainerByGUID(articleContainer.articleGUID, this.selectedArticlesOnRail)) {
        continue;
      }
      const articleLine: Line = this.insertHelper.getArticleLine(articleContainer);
      if (!articleLine || this.insertHelper.getDistanceFromStart(commonLine.end) > this.insertHelper.getDistanceFromStart(articleLine.end)) {
        continue;
      }
      if (!nearestLine || articleLine.start.distanceTo(commonLine.end) < nearestLine.start.distanceTo(commonLine.end)) {
        nearestLine = articleLine;
        nearestArticle = articleContainer;
      }
    }
    return nearestArticle;
  }
}
